import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CheckCircleIcon,
  EnvelopeIcon,
  ClockIcon,
  ArrowRightIcon,
  HeartIcon,
  SparklesIcon,
  PhoneIcon,
  HomeIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const EnquiryThankYouPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const packageName = searchParams.get('package') || 'Bali Package';
  const customerName = searchParams.get('name') || 'Valued Customer';

  return (
    <>
      <Helmet>
        <title>Thank You for Your Enquiry | Bali Getaway</title>
        <meta name="description" content="Thank you for your enquiry. We'll be in touch with you shortly." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center px-4 py-16">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl w-full mx-auto"
        >
          {/* Success Animation */}
          <div className="text-center mb-16">
            <div className="relative inline-block">
              <motion.div 
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15 }}
                className="w-32 h-32 bg-gradient-to-br from-green-400 to-emerald-500 rounded-[2.5rem] flex items-center justify-center mx-auto mb-8 shadow-2xl"
              >
                <CheckCircleIcon className="w-16 h-16 text-white" />
              </motion.div>
              <motion.div 
                animate={{ 
                  rotate: [0, 10, -10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ repeat: Infinity, duration: 4 }}
                className="absolute -top-4 -right-4"
              >
                <HeartIcon className="w-12 h-12 text-pink-500 fill-current drop-shadow-md" />
              </motion.div>
            </div>
            
            <h1 className="text-5xl lg:text-7xl font-black text-gray-900 mb-6 tracking-tight">
              Thank You, {customerName}! 🌺
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed font-medium">
              We've received your enquiry for <span className="text-gray-900 font-bold">{packageName}</span>. Our experts are already looking into it!
            </p>
            <div className="inline-flex items-center px-8 py-3 bg-green-100 text-green-800 rounded-2xl text-lg font-black uppercase tracking-tighter border border-green-200 shadow-sm">
              <ShieldCheckIcon className="w-6 h-6 mr-3" />
              Enquiry Received
            </div>
          </div>

          {/* Main Content Card */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-gray-100 mb-12"
          >
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-10 flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-black mb-2 tracking-tight">What Happens Next?</h2>
                <p className="text-blue-100 text-lg font-medium opacity-90">Our simple 3-step process</p>
              </div>
              <div className="hidden md:block">
                <SparklesIcon className="w-16 h-16 text-white/20" />
              </div>
            </div>

            <div className="p-10 lg:p-12">
              <div className="space-y-10">
                <motion.div whileHover={{ x: 10 }} className="flex items-start gap-6 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-blue-100 group-hover:scale-110 transition-transform">
                    <ClockIcon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-gray-900 mb-2 uppercase tracking-tight">Step 1: Rapid Review</h3>
                    <p className="text-gray-500 text-lg font-medium leading-relaxed">Our Bali travel specialists will review your specific preferences within <span className="text-blue-600 font-bold">2-4 business hours</span>.</p>
                  </div>
                </motion.div>

                <motion.div whileHover={{ x: 10 }} className="flex items-start gap-6 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-100 group-hover:scale-110 transition-transform">
                    <PhoneIcon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-gray-900 mb-2 uppercase tracking-tight">Step 2: Personal Consultation</h3>
                    <p className="text-gray-500 text-lg font-medium leading-relaxed">We'll reach out via your preferred contact method to discuss custom options and exclusive resort upgrades.</p>
                  </div>
                </motion.div>

                <motion.div whileHover={{ x: 10 }} className="flex items-start gap-6 group">
                  <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-100 group-hover:scale-110 transition-transform">
                    <EnvelopeIcon className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-black text-gray-900 mb-2 uppercase tracking-tight">Step 3: Tailored Proposal</h3>
                    <p className="text-gray-500 text-lg font-medium leading-relaxed">Receive a comprehensive, no-obligation quote with the best guaranteed pricing for your dream Bali stay.</p>
                  </div>
                </motion.div>
              </div>

              {/* Contact Information */}
              <div className="mt-12 bg-gradient-to-br from-orange-500 to-yellow-500 rounded-[2rem] p-8 text-white shadow-2xl shadow-orange-100 relative overflow-hidden group">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white/20 via-transparent to-transparent"></div>
                <h3 className="text-xl font-black mb-6 text-center uppercase tracking-widest relative">Need Immediate Assistance?</h3>
                <div className="flex flex-col md:flex-row items-center justify-center gap-6 relative">
                  <a href="mailto:enquiry@baligetaway.com.au" className="flex items-center gap-4 bg-white/20 backdrop-blur-md px-6 py-3 rounded-2xl hover:bg-white/30 transition-all border border-white/20">
                    <EnvelopeIcon className="w-6 h-6" />
                    <span className="font-black text-lg">enquiry@baligetaway.com.au</span>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Why Choose Us */}
          <div className="bg-white rounded-[3rem] shadow-xl p-12 mb-12 border border-gray-100">
            <h3 className="text-2xl font-black text-gray-900 mb-10 text-center tracking-tight uppercase">Why Trust Bali Getaway?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <div className="text-center group">
                <div className="w-20 h-20 bg-gray-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-gray-100 group-hover:bg-orange-50 transition-all group-hover:scale-110">
                  <div className="text-4xl group-hover:scale-110 transition-transform">🏆</div>
                </div>
                <h4 className="font-black text-gray-900 mb-2 text-lg uppercase tracking-tighter">15+ Years Expert Knowledge</h4>
                <p className="text-sm text-gray-500 font-medium">Unrivaled local expertise since 2010.</p>
              </div>
              
              <div className="text-center group">
                <div className="w-20 h-20 bg-gray-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-gray-100 group-hover:bg-orange-50 transition-all group-hover:scale-110">
                  <div className="text-4xl group-hover:scale-110 transition-transform">💰</div>
                </div>
                <h4 className="font-black text-gray-900 mb-2 text-lg uppercase tracking-tighter">Best Price Guaranteed</h4>
                <p className="text-sm text-gray-500 font-medium">Exclusive direct-to-resort contracts.</p>
              </div>
              
              <div className="text-center group">
                <div className="w-20 h-20 bg-gray-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-gray-100 group-hover:bg-orange-50 transition-all group-hover:scale-110">
                  <div className="text-4xl group-hover:scale-110 transition-transform">🎯</div>
                </div>
                <h4 className="font-black text-gray-900 mb-2 text-lg uppercase tracking-tighter">Personalized Service</h4>
                <p className="text-sm text-gray-500 font-medium">Tailored itineraries for your style.</p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16">
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/packages"
                className="inline-flex items-center justify-center px-10 py-5 bg-white text-gray-900 font-black rounded-[2rem] hover:bg-gray-50 transition-all shadow-xl border border-gray-100 uppercase tracking-tight"
              >
                <ArrowRightIcon className="mr-3 w-6 h-6 text-gray-400" />
                EXPLORE PACKAGES
              </Link>
            </motion.div>
            
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/"
                className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-gray-800 to-gray-900 text-white font-black rounded-[2rem] transition-all shadow-2xl shadow-gray-200 uppercase tracking-tight"
              >
                <HomeIcon className="mr-3 w-6 h-6" />
                BACK TO HOME
              </Link>
            </motion.div>
          </div>

          {/* Footer Note */}
          <div className="text-center pt-8 border-t border-gray-200/60">
            <p className="text-gray-500 font-medium text-lg leading-relaxed">
              Didn't receive a confirmation email within 10 minutes? <br className="hidden md:block" />
              Check your spam folder or{' '}
              <Link to="/contact" className="text-orange-600 hover:text-orange-700 font-black underline">
                contact us directly
              </Link>
            </p>
          </div>
        </motion.div>
      </div>
    </>
  );
};

export default EnquiryThankYouPage;
